/**
 * Control. Everything the five-tab bar cannot hold, plus the account.
 *
 * This was "More", reachable only from a phone, which meant a desktop operator
 * found these screens through the rail and a phone operator found them here —
 * and only one of the two was ever described anywhere. Now there is one route
 * to each of them.
 *
 * Decisions and incidents lead, and carry their count: they are the only entry
 * here that is ever urgent, and burying an open incident behind a settings menu
 * is exactly the failure this product exists to prevent.
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Panel } from '../components/Primitives';

interface Section {
  to: string;
  label: string;
  detail: string;
  /** Only the operational ones; administration does not get to look urgent. */
  counted?: boolean;
}

const OPERATIONS: Section[] = [
  {
    to: '/alerts',
    label: 'Decisions & incidents',
    detail: 'Everything open that needs a person, newest and most severe first.',
    counted: true,
  },
  {
    to: '/agents',
    label: 'Agent operations',
    detail: 'Workers, their sessions and what they are waiting on.',
  },
  {
    to: '/forecast',
    label: 'Forecast & warnings',
    detail: 'Where capacity runs out, and what that costs which journey.',
  },
];

const ADMINISTRATION: Section[] = [
  {
    to: '/integrations',
    label: 'Integrations',
    detail: 'Connections, permissions, sync health and remediation.',
  },
  {
    to: '/accounts',
    label: 'Accounts',
    detail: 'Who can sign in, what they may do, and how access is withdrawn.',
  },
  {
    to: '/audit',
    label: 'Settings & audit',
    detail: 'Operational metrics and the append-only audit trail.',
  },
];

function Sections({
  sections,
  interventions,
  critical,
}: {
  sections: Section[];
  interventions: number;
  critical: number;
}) {
  return (
    <div className="stack">
      {sections.map((section) => (
        <Link
          key={section.to}
          to={section.to}
          className="card card--interactive"
          style={{ display: 'block' }}
        >
          <div className="spread">
            <strong>{section.label}</strong>
            {section.counted && interventions > 0 && (
              <span className="chip" data-tone={critical > 0 ? 'critical' : 'risk'}>
                {interventions} open
              </span>
            )}
          </div>
          <p className="small dim" style={{ marginTop: 4 }}>
            {section.detail}
          </p>
        </Link>
      ))}
    </div>
  );
}

export function Control({
  interventions = 0,
  critical = 0,
}: {
  interventions?: number;
  critical?: number;
}) {
  const { user, signOut } = useAuth();
  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">{user?.tenantSlug}</div>
          <h1 className="topstrip__title">Control</h1>
        </div>
      </header>

      <Panel title="Operations">
        <Sections sections={OPERATIONS} interventions={interventions} critical={critical} />
      </Panel>

      <Panel title="Administration">
        <Sections sections={ADMINISTRATION} interventions={interventions} critical={critical} />
      </Panel>

      <Panel title="Demonstration">
        <div className="stack">
          <p className="small dim">
            A simulated fleet for screenshots and walkthroughs: invented projects, agents, leases
            and capacity, on the same screens. It makes no request to the bridge and takes no lease,
            so nothing in it can reach live data — and it is marked on every screen so it can never
            be mistaken for it.
          </p>
          <Link className="btn btn--block" to="/demo">
            Open demo mode
          </Link>
        </div>
      </Panel>

      <Panel title="Account">
        <div className="stack">
          <div>
            <div className="label">Signed in as</div>
            <p>{user?.displayName}</p>
            <p className="small dim">
              {user?.email} · {user?.role.replace('_', ' ')}
            </p>
          </div>
          <button type="button" className="btn btn--block" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </Panel>
    </>
  );
}
